import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { runGated } from "@/lib/adsGate";
import { runAiJob } from "@/lib/aiJobs";
import { useAuth } from "@/lib/AuthProvider";
import { showInterstitial } from "@/lib/admob";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TEST_IDS } from "@/constants/testIds";
import { motion } from "framer-motion";
import { Loader2, RefreshCw, Sparkles, Bookmark } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import SaveButton from "@/components/SaveButton";

const OCCASIONS = [
  "Office","College","Casual","Party","Wedding","Travel","Festival",
  "Date","Business Meeting","Gym",
];

export default function OutfitGenerator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [occasion, setOccasion] = useState("Casual");
  const [loading, setLoading] = useState(false);
  const [outfit, setOutfit] = useState(null);
  const [swapping, setSwapping] = useState(null);
  const [tryon, setTryon] = useState(null);
  const [tryonLoading, setTryonLoading] = useState(false);
  // Track outfit generations per session so we only fire an interstitial
  // every 3rd successful generate for free users. Prevents ad spam while
  // still monetising engaged non-paying sessions.
  const genCountRef = useRef(0);
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    api.get("/collections").then(({ data }) => setCollections(data)).catch(() => {});
  }, []);

  const saveToCollection = async (cid) => {
    if (!outfit?.id) return;
    try {
      await api.post(`/collections/${cid}/add`, { outfit_id: outfit.id });
      toast.success("Saved to collection");
    } catch { toast.error("Could not save"); }
  };

  // Removed: emergency outfit auto-load (feature retired)

  const collectIds = (o) => [
    o?.top?.id, o?.bottom?.id, o?.shoes?.id,
    ...(o?.accessories || []).map((a) => a.id),
  ].filter(Boolean);

  const autoTryOn = async (o) => {
    const ids = collectIds(o);
    if (!ids.length) return;
    setTryonLoading(true);
    setTryon(null);
    try {
      const fd = new FormData();
      fd.append("item_ids", ids.join(","));
      fd.append("background", "studio");
      // /tryon is async — runAiJob polls the ai_jobs row until Nano Banana
      // finishes so the auto try-on still appears progressively even for
      // 60-90s renders that would otherwise die on Cloudflare's 100s cap.
      const data = await runGated("tryons", () =>
        runAiJob(
          () => api.post("/tryon", fd, { headers: { "Content-Type": "multipart/form-data" } }),
        ),
      );
      setTryon(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "quota_exceeded") {
        toast.info("Daily try-on quota reached. Upgrade for unlimited.", {
          action: { label: "Plans", onClick: () => navigate("/plans") },
        });
      } else if (e.response?.status === 400 && (detail || "").includes("No user photo")) {
        toast.info("Add a body photo in your profile to enable auto try-on.");
      }
    } finally { setTryonLoading(false); }
  };

  const generate = async () => {
    setLoading(true);
    setTryon(null);
    try {
      const { data } = await api.post("/outfits/generate", { occasion });
      setOutfit(data);
      autoTryOn(data); // fire-and-forget — try-on appears progressively
      // Interstitial every 3rd generation for free users only. No-op on web
      // (Capacitor guard inside admob.js).
      genCountRef.current += 1;
      const isFree = !user?.subscription_tier || user.subscription_tier === "free";
      if (isFree && genCountRef.current % 3 === 0) {
        showInterstitial().catch(() => {});
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "quota_exceeded") {
        toast.error(detail.message || "Daily quota hit", {
          action: { label: "Upgrade", onClick: () => navigate("/plans") },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not generate outfit");
      }
    } finally { setLoading(false); }
  };

  const swap = async (slot) => {
    if (!outfit) return;
    setSwapping(slot);
    try {
      const currentId =
        slot === "top" ? outfit.top?.id :
        slot === "bottom" ? outfit.bottom?.id :
        slot === "shoes" ? outfit.shoes?.id :
        outfit.accessories?.[0]?.id;
      const { data: newItem } = await api.post("/outfits/swap", {
        slot, occasion, exclude_ids: currentId ? [currentId] : [],
      });
      const next = { ...outfit };
      if (slot === "top") next.top = newItem;
      else if (slot === "bottom") next.bottom = newItem;
      else if (slot === "shoes") next.shoes = newItem;
      else next.accessories = [newItem, ...(outfit.accessories || []).slice(1)];
      setOutfit(next);
      autoTryOn(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No alternative available");
    } finally { setSwapping(null); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      <p className="eyebrow">AI Outfit Generator</p>
      <h1 className="editorial-h1 mt-3">A look for the occasion.</h1>

      <div className="mt-10 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 border border-border p-6 h-fit">
          <p className="eyebrow">Occasion</p>
          <Select value={occasion} onValueChange={setOccasion}>
            <SelectTrigger data-testid={TEST_IDS.outfit.occasionSelect} className="rounded-none mt-3 h-12">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OCCASIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>

          <Button
            data-testid={TEST_IDS.outfit.generateBtn}
            onClick={generate}
            disabled={loading}
            className="mt-6 w-full h-12 rounded-none uppercase tracking-[0.2em] text-xs"
          >
            {loading ? <><Loader2 size={14} className="animate-spin mr-2" /> Styling…</> : "Generate look"}
          </Button>

          <p className="text-xs text-muted-foreground mt-6 leading-relaxed">
            Stylebay picks from your available wardrobe. Auto try-on runs alongside.
          </p>
        </div>

        <div className="lg:col-span-8 space-y-6" data-testid={TEST_IDS.outfit.result}>
          {!outfit && !loading && (
            <div className="border border-dashed border-border p-16 text-center">
              <p className="font-serif text-3xl">Press generate to see a curated look.</p>
              <p className="text-sm text-muted-foreground mt-4">Includes a Try-On generation alongside.</p>
            </div>
          )}
          {loading && (
            <div className="border border-border p-16 text-center">
              <Loader2 size={28} className="mx-auto animate-spin" />
              <p className="eyebrow mt-4 pulse-soft">Composing your look</p>
            </div>
          )}
          {outfit && !loading && (
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <OutfitTile label="Top" item={outfit.top} slot="top" onSwap={swap} swapping={swapping === "top"} />
                <OutfitTile label="Bottom" item={outfit.bottom} slot="bottom" onSwap={swap} swapping={swapping === "bottom"} />
                <OutfitTile label="Shoes" item={outfit.shoes} slot="shoes" onSwap={swap} swapping={swapping === "shoes"} />
                <OutfitTile label="Accessory" item={outfit.accessories?.[0]} slot="accessory" onSwap={swap} swapping={swapping === "accessory"} />
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  onClick={generate}
                  data-testid="regenerate-outfit-btn"
                  variant="outline"
                  className="rounded-none uppercase tracking-[0.18em] text-[10px] h-10"
                >
                  <RefreshCw size={12} className="mr-2" /> Generate a different look
                </Button>
                <SaveButton
                  itemType="outfit"
                  itemId={outfit.id}
                  snapshot={{
                    occasion: outfit.occasion,
                    top: outfit.top, bottom: outfit.bottom, shoes: outfit.shoes,
                    style_score: outfit.style_score,
                  }}
                  variant="chip"
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      data-testid="save-to-collection-btn"
                      variant="outline"
                      className="rounded-none uppercase tracking-[0.18em] text-[10px] h-10"
                    >
                      <Bookmark size={12} className="mr-2" /> Add to collection
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="rounded-none">
                    {collections.length === 0 && (
                      <DropdownMenuItem disabled>No collections yet</DropdownMenuItem>
                    )}
                    {collections.map((c) => (
                      <DropdownMenuItem
                        key={c.id}
                        data-testid={`save-to-${c.id}`}
                        onClick={() => saveToCollection(c.id)}
                      >{c.name}</DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="mt-6 border border-border p-6">
                <p className="eyebrow">Stylist's note</p>
                <p className="font-serif text-2xl mt-3 leading-snug">{outfit.reasoning}</p>
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Score label="Style" value={outfit.style_score} />
                  <Score label="Confidence" value={outfit.confidence_score} />
                  <Score label="Colour Harmony" value={outfit.color_harmony_score} />
                  <Score label="Occasion Match" value={outfit.occasion_match_score} />
                </div>
              </div>

              {/* Auto-generated try-on */}
              <div className="mt-6 border border-border p-6" data-testid="auto-tryon-block">
                <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> Auto try-on</p>
                {tryonLoading && (
                  <div className="mt-4 aspect-[3/4] bg-muted flex flex-col items-center justify-center gap-3 max-w-xs">
                    <Loader2 size={26} className="animate-spin" />
                    <p className="eyebrow pulse-soft">Composing on you</p>
                  </div>
                )}
                {!tryonLoading && tryon && (
                  <div className="mt-4 grid md:grid-cols-2 gap-4 items-start">
                    <img src={fileUrl(tryon.result_path)} alt="Try-on" className="w-full" data-testid="auto-tryon-image" />
                    <div className="text-sm">
                      <p className="font-serif text-2xl">See it on you.</p>
                      <p className="mt-3 text-muted-foreground">Refine the look by tapping <RefreshCw size={12} className="inline" /> on any piece — the try-on regenerates automatically.</p>
                      <Button onClick={() => navigate("/tryon")} variant="outline"
                              className="mt-4 rounded-none uppercase tracking-[0.2em] text-[10px] h-10">
                        Open in Try-On Studio
                      </Button>
                    </div>
                  </div>
                )}
                {!tryonLoading && !tryon && (
                  <p className="mt-3 text-sm text-muted-foreground">
                    Add a body photo in your profile to enable the automatic try-on next time.
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

function OutfitTile({ label, item, slot, onSwap, swapping }) {
  return (
    <div className="border border-border bg-card relative">
      <div className="aspect-[3/4] bg-muted overflow-hidden">
        {item ? (
          <img src={fileUrl(item.image_path)} alt={item.type} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full flex items-center justify-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">none</div>
        )}
      </div>
      <div className="p-3">
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
        <p className="text-sm mt-1 truncate">{item?.type || "—"}</p>
      </div>
      {item && onSwap && (
        <button
          data-testid={`outfit-swap-${slot}`}
          onClick={() => onSwap(slot)}
          disabled={swapping}
          aria-label={`Swap ${label}`}
          className="absolute top-2 right-2 p-1.5 bg-background/80 backdrop-blur border border-border hover:bg-background transition"
        >
          {swapping ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
        </button>
      )}
    </div>
  );
}

function Score({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-serif text-4xl">{value || 0}</span>
        <span className="text-[10px] text-muted-foreground">/100</span>
      </div>
      <div className="mt-2 h-px bg-border relative">
        <div className="absolute inset-y-0 left-0 bg-accent" style={{ width: `${value || 0}%`, height: "1px" }} />
      </div>
    </div>
  );
}
